import { useCallback } from 'react';
import { ModuleKey } from '@interfaces/Permissions';
import useModules from './useModules';

/**
 * Explicit state of a module gate. `hasModule()` is tri-state (`boolean | null`)
 * and collapsing it with `!hasModule(X)` paints "don't know yet" exactly like
 * "you don't have it": loading, denied and failed-fetch become indistinguishable.
 */
export type ModuleGateState = 'loading' | 'granted' | 'denied';

// Resolves the tri-state following the `ProductsV2.tsx` precedent: while modules
// are loading -> `loading`; once loaded, a `null` (empty list because the plan is
// missing from the catalog, or because the `account-details` fetch failed)
// resolves to `denied` -> offer, NOT to a permanent spinner.
export const resolveModuleGate = (isModulesLoading: boolean, hasModuleResult: boolean | null): ModuleGateState => {
  if (isModulesLoading) return 'loading';
  return hasModuleResult === true ? 'granted' : 'denied';
};

export const useModuleGate = () => {
  const { hasModule, isModulesLoading } = useModules();

  const moduleGate = useCallback(
    (module: ModuleKey): ModuleGateState => resolveModuleGate(isModulesLoading, hasModule(module)),
    [hasModule, isModulesLoading],
  );

  return { moduleGate, isModulesLoading };
};

export default useModuleGate;
